<script setup lang="ts">
import {computed} from 'vue';
import type {Homework} from '@/ts/types/dto/interventions-dto';
import BadgePill from '@/vue/atoms/badge-pill.vue';
import BalmBtn from '@/vue/atoms/balm-btn.vue';
import SvgIllustration from '@/vue/atoms/svg-illustration.vue';

const props = defineProps<{
  homework: Homework;
  variant: 'carousel' | 'detail';
  durationMinutes?: number;
}>();

defineEmits<{
  openHomework: [];
}>();

const isCompleted = computed(() => Boolean(props.homework.completedDate));
const homeworkPage = computed(() => props.homework.pages[0] ?? null);
const title = computed(() => homeworkPage.value?.name ?? '');
const description = computed(() => homeworkPage.value?.description ?? '');
</script>

<template>
  <div
    v-if="homeworkPage"
    class="homework-card"
    :class="[variant]"
    @click="$emit('openHomework')"
  >
    <!-- Carousel variant -->
    <template v-if="variant === 'carousel'">
      <badge-pill
        theme="green"
        class="carousel-badge"
        :label="isCompleted ? $t('completed') : $t('homework')"
      >
        <svg-illustration v-if="isCompleted" name="check.svg" class="badge-icon"/>
      </badge-pill>
      <p v-safe-html="title" class="carousel-title"/>
      <div class="carousel-footer">
        <div v-if="isCompleted" class="carousel-completed">
          <svg-illustration name="check.svg"/>
        </div>
        <svg-illustration name="arrow-narrow-up-right.svg" class="carousel-arrow"/>
      </div>
    </template>

    <!-- Detail variant -->
    <template v-else>
      <badge-pill
        theme="green"
        class="detail-badge"
        :label="isCompleted ? $t('completed') : $t('homework')"
      >
        <svg-illustration v-if="isCompleted" name="check.svg" class="badge-icon"/>
      </badge-pill>
      <div class="detail-content">
        <h3 v-safe-html="title" class="detail-title"/>
        <div v-if="description" v-safe-html="description" class="detail-description"/>
      </div>
      <div class="detail-footer">
        <span v-if="durationMinutes" class="detail-duration">
          {{ $t('homework_n_min', {n: durationMinutes}) }}
        </span>
        <balm-btn
          paddings="p2-5"
          theme="outline"
          class="detail-btn"
          :label="isCompleted ? $t('completed') : $t('homework_start')"
          :icon="isCompleted ? 'check.svg' : 'arrow-narrow-up-right.svg'"
          icon-position="right"
        />
      </div>
    </template>
  </div>
</template>

<style scoped lang="scss">
.homework-card {
  background: var(--Light-Scheme-Functional-wl-complete-surface);
  color: var(--Light-Scheme-Neutral-UI-neutral-ui-100);
  border-radius: var(--card-border-radius);
  cursor: pointer;

}

// Carousel variant
.homework-card.carousel {
  width: calc(var(--Spacing-spacing-13) - var(--Spacing-spacing-4));
  height: var(--Spacing-spacing-13);
  padding: var(--Spacing-spacing-4) var(--Spacing-spacing-4) var(--Spacing-spacing-5) var(--Spacing-spacing-4);
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  overflow: hidden;
}

.homework-card.carousel :deep(.carousel-badge) {
  align-self: flex-start;
}

.carousel-title {
  @include font-body-sm-bold;
  margin: var(--Spacing-spacing-2) 0 0 0;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  overflow: hidden;
}

.carousel-footer {
  margin-top: auto;
  align-self: stretch;
  display: flex;
  align-items: flex-end;
  justify-content: flex-end;
  gap: var(--Spacing-spacing-6);
}

.carousel-completed {
  @include font-body-sm-bold;
  display: flex;
  align-items: center;
  gap: var(--Spacing-spacing-1);
}

// Detail variant
.homework-card.detail {
  padding: var(--Spacing-spacing-5);
  display: flex;
  flex-direction: column;
}

.detail-content {
  margin-top: var(--Spacing-spacing-3);
}

.detail-title {
  @include font-h3-bold;
  margin: 0;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  overflow: hidden;
}

.detail-description {
  @include font-body-sm;
  margin: var(--Spacing-spacing-3) 0 0 0;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
  line-clamp: 3;
  overflow: hidden;
}

.detail-footer {
  margin-top: var(--Spacing-spacing-7);
  display: flex;
  align-items: center;
}

.detail-duration {
  @include font-body-sm;
  margin-right: auto;
}

.homework-card.detail :deep(.detail-badge) {
  align-self: flex-start;
}

.homework-card.detail :deep(.detail-btn.balm-btn) {
  margin-left: auto;
  color: var(--Light-Scheme-Neutral-UI-neutral-ui-100);
  border-color: var(--Light-Scheme-Neutral-UI-neutral-ui-100);
}

.badge-icon {
  width: 16px;
  height: 16px;
}
</style>
